import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

interface LocationMapProps {
  latitude: number;
  longitude: number;
  tzOffsetMinutes: number;
  onPick: (lat: number, lon: number) => void;
}

interface MapParts {
  map: L.Map;
  marker: L.Marker;
  overlays: L.LayerGroup;
}

const TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const pinIcon = () =>
  L.divIcon({
    className: "solar-pin",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    html: `<div style="width:18px;height:18px;border-radius:50%;background:#c0501b;border:3px solid #f6efe3;box-shadow:0 0 0 1px #c0501b;"></div>`,
  });

const meridianLabelIcon = (lon: number) =>
  L.divIcon({
    className: "solar-meridian-label",
    iconSize: [160, 20],
    iconAnchor: [0, 10],
    html: `<div style="font-family:'Fraunces Variable',serif;font-style:italic;font-size:12px;color:#2b1f14;background:#f6efe3cc;padding:2px 8px;border-radius:2px;white-space:nowrap;">standard meridian · ${lon.toFixed(0)}°</div>`,
  });

function createMap(el: HTMLElement, center: L.LatLngTuple, onPick: (lat: number, lon: number) => void): MapParts {
  const map = L.map(el, { zoomControl: true, worldCopyJump: true }).setView(center, 3);
  L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 18 }).addTo(map);
  const marker = L.marker(center, { icon: pinIcon(), draggable: true }).addTo(map);
  marker.on("dragend", () => {
    const p = marker.getLatLng();
    onPick(p.lat, p.lng);
  });
  map.on("click", (e: L.LeafletMouseEvent) => onPick(e.latlng.lat, e.latlng.lng));
  const overlays = L.layerGroup().addTo(map);
  setTimeout(() => map.invalidateSize(), 0);
  return { map, marker, overlays };
}

// Standard-meridian line + label, and a connector from the pin to the meridian.
function drawMeridian(group: L.LayerGroup, lat: number, lon: number, meridianLon: number) {
  group.clearLayers();
  L.polyline(
    [
      [85, meridianLon],
      [-85, meridianLon],
    ],
    { color: "#2b1f14", weight: 2, opacity: 0.75, dashArray: "6 6" },
  ).addTo(group);
  const labelLat = Math.max(-70, Math.min(70, lat + 6));
  L.marker([labelLat, meridianLon], { interactive: false, icon: meridianLabelIcon(meridianLon) }).addTo(group);
  L.polyline(
    [
      [lat, lon],
      [lat, meridianLon],
    ],
    { color: "#c0501b", weight: 1.5, opacity: 0.75, dashArray: "2 5" },
  ).addTo(group);
}

export default function LocationMap({ latitude, longitude, tzOffsetMinutes, onPick }: LocationMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const partsRef = useRef<MapParts | null>(null);
  const onPickRef = useRef(onPick);
  const initialCenter = useRef<L.LatLngTuple>([latitude, longitude]);

  // Always call the latest onPick, even though the map's handlers are bound once.
  useEffect(() => {
    onPickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    if (!containerRef.current) return;
    const parts = createMap(containerRef.current, initialCenter.current, (lat, lon) => onPickRef.current(lat, lon));
    partsRef.current = parts;
    return () => {
      parts.map.remove();
      partsRef.current = null;
    };
  }, []);

  useEffect(() => {
    const parts = partsRef.current;
    if (!parts) return;
    parts.marker.setLatLng([latitude, longitude]);
    drawMeridian(parts.overlays, latitude, longitude, (tzOffsetMinutes / 60) * 15);
  }, [latitude, longitude, tzOffsetMinutes]);

  return (
    <div
      ref={containerRef}
      data-testid="solar-map"
      className="h-[360px] w-full overflow-hidden rounded-sm border border-[#d9c9ad]"
    />
  );
}
